import { useEffect, useState } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3500';

type Group = { id: string; name: string; color?: string | null; clusters: string[] };

const DEFAULT_COLORS = ['#5b5bd6', '#16a34a', '#dc2626', '#ea580c', '#0891b2', '#9333ea', '#ca8a04', '#475569'];

export default function Groups({ apiKey }: { apiKey: string }) {
  const [groups, setGroups] = useState<Group[]>([]);
  const [clusters, setClusters] = useState<string[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const [status, setStatus] = useState('');
  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState('');

  const authHeaders = { 'X-API-Key': apiKey };

  const fetchAll = async () => {
    try {
      const [gRes, cRes] = await Promise.all([
        fetch(`${API_URL}/groups`, { headers: authHeaders }),
        fetch(`${API_URL}/clusters`, { headers: authHeaders }),
      ]);
      const gData = await gRes.json();
      const cData = await cRes.json();
      setGroups(gData.groups || []);
      setClusters(cData.clusters || []);
    } catch {
      setStatus('Cannot connect to server.');
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const selected = groups.find((g) => g.id === selectedId) || null;

  const createGroup = async () => {
    const name = newName.trim();
    if (!name) return;
    const color = DEFAULT_COLORS[groups.length % DEFAULT_COLORS.length];
    try {
      const res = await fetch(`${API_URL}/groups`, {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, color }),
      });
      const data = await res.json();
      if (res.ok) {
        setNewName('');
        setCreating(false);
        await fetchAll();
        setSelectedId(data.id);
      } else {
        setStatus(data.detail || 'Failed to create group');
      }
    } catch {
      setStatus('Cannot connect to server.');
    }
  };

  const deleteGroup = async (id: string, name: string) => {
    if (!window.confirm(`Удалить группу "${name}"? Кластеры останутся, потеряют только этот тег.`)) return;
    try {
      await fetch(`${API_URL}/groups/${id}`, { method: 'DELETE', headers: authHeaders });
      if (selectedId === id) setSelectedId(null);
      await fetchAll();
    } catch {
      setStatus('Cannot connect to server.');
    }
  };

  const toggleCluster = async (cluster: string) => {
    if (!selected) return;
    const inGroup = selected.clusters.includes(cluster);
    try {
      if (inGroup) {
        await fetch(`${API_URL}/groups/${selected.id}/clusters/${encodeURIComponent(cluster)}`, {
          method: 'DELETE',
          headers: authHeaders,
        });
      } else {
        await fetch(`${API_URL}/groups/${selected.id}/clusters`, {
          method: 'POST',
          headers: { ...authHeaders, 'Content-Type': 'application/json' },
          body: JSON.stringify({ cluster }),
        });
      }
      await fetchAll();
    } catch {
      setStatus('Cannot connect to server.');
    }
  };

  const setColor = async (id: string, color: string) => {
    try {
      await fetch(`${API_URL}/groups/${id}`, {
        method: 'PUT',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ color }),
      });
      await fetchAll();
    } catch {}
  };

  const renameGroup = async () => {
    if (!selected) return;
    const name = nameInput.trim();
    if (!name || name === selected.name) {
      setEditingName(false);
      return;
    }
    try {
      const res = await fetch(`${API_URL}/groups/${selected.id}`, {
        method: 'PUT',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name }),
      });
      const data = await res.json();
      if (res.ok) {
        setEditingName(false);
        await fetchAll();
      } else {
        setStatus(data.detail || 'Failed to rename');
      }
    } catch {
      setStatus('Cannot connect to server.');
    }
  };

  return (
    <div>
      <h2>Группы</h2>
      <p style={{ color: 'var(--text-muted)', margin: '0 0 16px' }}>
        Группировка кластеров по тегам. Используется для фильтра на странице «Загрузка». На отчёты, Сводный, Сравнение и Зарплату не влияет.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(260px, 1fr) 2fr', gap: 20, alignItems: 'start' }}>
        {/* Left: groups list */}
        <div>
          <div className="cluster-list">
            {groups.map((g) => (
              <div
                key={g.id}
                className={`cluster-item ${selectedId === g.id ? 'cluster-active' : ''}`}
                onClick={() => { setSelectedId(g.id); setEditingName(false); }}
                style={{ cursor: 'pointer' }}
              >
                <span
                  style={{
                    width: 12, height: 12, borderRadius: 3, background: g.color || '#888',
                    marginRight: 8, flexShrink: 0,
                  }}
                />
                <span className="cluster-name">{g.name}</span>
                <span style={{ color: 'var(--text-faint)', fontSize: 12, marginRight: 8 }}>
                  {g.clusters.length}
                </span>
                <button
                  className="cluster-delete"
                  onClick={(e) => { e.stopPropagation(); deleteGroup(g.id, g.name); }}
                  title="Удалить группу"
                >
                  &times;
                </button>
              </div>
            ))}
            {!creating ? (
              <button className="cluster-add" onClick={() => setCreating(true)}>
                + Добавить группу
              </button>
            ) : (
              <div className="cluster-new">
                <input
                  type="text"
                  placeholder="Название группы (напр. 1 квартал 2026)"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') createGroup();
                    if (e.key === 'Escape') { setCreating(false); setNewName(''); }
                  }}
                  autoFocus
                />
                <button onClick={createGroup}>Создать</button>
                <button onClick={() => { setCreating(false); setNewName(''); }}>Отмена</button>
              </div>
            )}
          </div>
        </div>

        {/* Right: selected group's cluster picker */}
        <div>
          {selected ? (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
                {editingName ? (
                  <>
                    <input
                      type="text"
                      value={nameInput}
                      onChange={(e) => setNameInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') renameGroup();
                        if (e.key === 'Escape') setEditingName(false);
                      }}
                      autoFocus
                      style={{ padding: '6px 8px', fontSize: 16 }}
                    />
                    <button onClick={renameGroup}>Сохранить</button>
                    <button onClick={() => setEditingName(false)}>Отмена</button>
                  </>
                ) : (
                  <>
                    <h3
                      style={{ margin: 0, cursor: 'pointer' }}
                      onClick={() => { setNameInput(selected.name); setEditingName(true); }}
                      title="Нажмите, чтобы переименовать"
                    >
                      {selected.name}
                    </h3>
                    <div style={{ display: 'flex', gap: 4 }}>
                      {DEFAULT_COLORS.map((c) => (
                        <button
                          key={c}
                          onClick={() => setColor(selected.id, c)}
                          style={{
                            width: 18, height: 18, borderRadius: 4, background: c,
                            border: selected.color === c ? '2px solid var(--text)' : '1px solid var(--border)',
                            cursor: 'pointer', padding: 0,
                          }}
                          title={c}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>

              <div style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 8 }}>
                Отметьте кластеры, чтобы добавить/убрать из группы:
              </div>
              <div className="cluster-list">
                {clusters.map((c) => {
                  const checked = selected.clusters.includes(c);
                  return (
                    <div
                      key={c}
                      className={`cluster-item ${checked ? 'cluster-active' : ''}`}
                      onClick={() => toggleCluster(c)}
                      style={{ cursor: 'pointer' }}
                    >
                      <input
                        type="checkbox"
                        className="default-checkbox"
                        checked={checked}
                        onChange={() => toggleCluster(c)}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <span className="cluster-name">{c}</span>
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <div style={{ color: 'var(--text-muted)', padding: 32, textAlign: 'center' }}>
              Выберите группу слева, чтобы изменить её кластеры, или создайте новую.
            </div>
          )}
        </div>
      </div>

      {status && <p className="status" style={{ marginTop: 16 }}>{status}</p>}
    </div>
  );
}
